import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildPurchaseRequisitionCard,
  PurchaseRequisitionSnapshot,
} from '@/components/Approval/@components/PurchaseRequisition';
import SearchableSelect from '@/components/Approval/@components/SearchableSelect';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getPurchaseRequisitionDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildPurchaseRequisitionCandidateLabel,
  buildPurchaseRequisitionEditorHtml,
  buildPurchaseRequisitionSubject,
  isPoChangeType,
  withPurchaseRequisitionCards,
  type PurchaseRequisitionCard,
  type PurchaseRequisitionCandidate,
  type PurchaseRequisitionDraftSource,
} from '@/utils/approval/purchaseRequisition';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = {
  /** 122 PR(제상품) 또는 129 PO(합의납기일/생산처) */
  apvType: 122 | 129;
  isEditMode: boolean;
  editorApi: ReplaceContentApi | null;
};

/**
 * GERP PR(제상품)(122)·PO(합의납기일/생산처)(129) — 미상신 건을 여러 건 체크하면 건마다 카드를 본문에 붙임.
 * 체크·해제·삭제 때마다 제목과 본문 에디터를 덮어씀. 편집은 건을 바꿀 수 없고 저장 본문만.
 * 129 는 레거시에 전체일괄추가가 없음
 */
const PurchaseRequisitionField = ({
  apvType,
  isEditMode,
  editorApi,
}: Props) => {
  const isPoChange = isPoChangeType(apvType);
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue, getValues } = useFormContext<ApprovalFormValues>();
  const loaded = useWatch({ control, name: 'purchaseRequisition' });
  const styleNoModalRef = useRef<ModalRef>(null);

  const { data: source, isLoading } = useSWR<PurchaseRequisitionDraftSource>(
    isEditMode
      ? null
      : getPurchaseRequisitionDraftSourceUrl(undefined, apvType),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  // 불러오는 중인 건과 그 요청 번호 — 셀렉트에는 체크된 채로 보이고, 해제하거나 더 늦게 다시 고르면 먼저 보낸 응답은 버림
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const pendingRef = useRef(new Map<string, number>());
  const requestSeqRef = useRef(0);

  const applyCards = (cards: PurchaseRequisitionCard[]) => {
    const first = candidates.find((c) => c.aprvId === cards[0]?.aprvId);
    const current = getValues('purchaseRequisition');
    const base = {
      systemId: first?.sysId ?? current?.systemId ?? '',
      // 레거시는 결재 유형을 122=PR, 129=CACP 로 고정
      approvalTypeCode:
        first?.aprvTypcd ||
        current?.approvalTypeCode ||
        (isPoChange ? 'CACP' : 'PR'),
    };
    setValue('purchaseRequisition', withPurchaseRequisitionCards(base, cards), {
      shouldDirty: true,
    });
    const styleNos = cards.map((c) => c.styleNo);
    setValue('subject', buildPurchaseRequisitionSubject(styleNos, t, apvType), {
      shouldDirty: false,
    });
    editorApi?.replace(buildPurchaseRequisitionEditorHtml(styleNos, t));
  };

  const currentCards = () => getValues('purchaseRequisition')?.cards ?? [];

  /** requestId 를 주면 그 요청이 건 표시만 지움 */
  const clearPending = (ids: string[], requestId?: number) => {
    ids.forEach((id) => {
      if (requestId === undefined || pendingRef.current.get(id) === requestId)
        pendingRef.current.delete(id);
    });
    setPendingIds(Array.from(pendingRef.current.keys()));
  };

  const addCards = async (targets: PurchaseRequisitionCandidate[]) => {
    const exists = new Set(currentCards().map((c) => c.aprvId));
    const ids = targets
      .map((c) => c.aprvId)
      .filter((id) => !exists.has(id) && !pendingRef.current.has(id));
    if (!ids.length) return;
    requestSeqRef.current += 1;
    const requestId = requestSeqRef.current;
    ids.forEach((id) => pendingRef.current.set(id, requestId));
    setPendingIds(Array.from(pendingRef.current.keys()));
    try {
      const data = await getNestedDataFetcher<PurchaseRequisitionDraftSource>(
        getPurchaseRequisitionDraftSourceUrl(ids, apvType),
      );
      const content = data.display.content;
      const wanted = ids.filter(
        (id) => pendingRef.current.get(id) === requestId,
      );
      const added = wanted
        .map((id) => content?.cards?.find((c) => c.aprvId === id))
        .filter((c): c is NonNullable<typeof c> => !!c)
        .map((c) =>
          buildPurchaseRequisitionCard(c, content?.partcodes, t, apvType),
        );
      if (added.length < wanted.length) {
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
      }
      if (added.length) applyCards([...currentCards(), ...added]);
    } catch {
      if (ids.some((id) => pendingRef.current.get(id) === requestId))
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      clearPending(ids, requestId);
    }
  };

  const removeCards = (ids: string[]) => {
    clearPending(ids);
    const cards = currentCards();
    const next = cards.filter((c) => !ids.includes(c.aprvId));
    if (next.length !== cards.length) applyCards(next);
  };

  const cards = loaded?.cards ?? [];
  const selectedIds = [...cards.map((c) => c.aprvId), ...pendingIds];

  const handleSelectChange = (next: string[]) => {
    const added = next.filter((id) => !selectedIds.includes(id));
    const removed = selectedIds.filter((id) => !next.includes(id));
    if (removed.length) removeCards(removed);
    if (added.length)
      addCards(candidates.filter((c) => added.includes(c.aprvId)));
  };

  const body = loaded?.content ? (
    <PurchaseRequisitionSnapshot
      content={loaded.content}
      renderCardAction={
        isEditMode
          ? undefined
          : ({ aprvId }) => (
              <BoxButton
                type="button"
                variant="secondary"
                size="small"
                onClick={() => removeCards([aprvId])}
              >
                {t('삭제')}
              </BoxButton>
            )
      }
    />
  ) : null;

  if (isEditMode) return <FieldBlock>{body}</FieldBlock>;

  const styleNos = cards.map((c) => c.styleNo);

  return (
    <FieldBlock>
      <ControlRow>
        <SearchableSelect
          multiple
          value={selectedIds}
          options={candidates.map((c) => ({
            value: c.aprvId,
            label: buildPurchaseRequisitionCandidateLabel(c),
          }))}
          placeholder={
            isLoading
              ? t('불러오는 중...')
              : candidates.length
              ? isPoChange
                ? t('PO를 선택하세요')
                : t('PR을 선택하세요')
              : t('진행할 내역이 없습니다.')
          }
          noOptionsText={t('진행할 내역이 없습니다.')}
          renderValue={(ids) =>
            ids
              .map((id) => {
                const c = candidates.find((x) => x.aprvId === id);
                return c ? buildPurchaseRequisitionCandidateLabel(c) : id;
              })
              .join(', ')
          }
          onChange={handleSelectChange}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        {pendingIds.length > 0 && <Spinner theme="black" size="16px" />}
        {!isPoChange && (
          <BoxButton
            type="button"
            variant="secondary"
            size="medium"
            disabled={!candidates.length}
            onClick={() => addCards(candidates)}
          >
            {t('전체일괄추가')}
          </BoxButton>
        )}
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          onClick={() => styleNoModalRef.current?.open()}
        >
          {t('추가 품번 확인')}
        </BoxButton>
      </ControlRow>
      {body}
      <Modal
        ref={styleNoModalRef}
        title={t('추가 품번 확인')}
        size="x-small"
        handleClose={() => styleNoModalRef.current?.close()}
      >
        <StyleNoList>
          {styleNos.map((styleNo, i) => (
            <Body2 key={`${styleNo}-${i}`} component="li">
              {styleNo}
            </Body2>
          ))}
        </StyleNoList>
      </Modal>
    </FieldBlock>
  );
};

const ControlRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    '& > .MuiInputBase-root, & > .MuiFormControl-root': { flexBasis: '100%' },
  },
}));

const StyleNoList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  '& > li + li': { marginTop: 4 },
});

export default PurchaseRequisitionField;
